import React, { useCallback, useMemo } from 'react'
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone'
import { flatten } from '../../../helpers/array'
import { sortStringsAlphabetically } from '../../../helpers/sorting'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import { Icon, IconChoices } from '../Icon'
import { SpinnerWrapper } from '../SpinnerWrapper'
import { getFileMimeType, toBase64 } from './helpers'

interface FileWithPath extends File {
  path?: string
}

export enum DropZoneVariant {
  MEDIA = 0,
  DOCUMENT = 1,
  EXCEL = 2,
  FOLDER_AND_DOCUMENTS = 3,
}

export interface DropZoneFile {
  base64: string
  name: string
  type: string
  uri: string
  path?: string
  size: number
}

interface DropZoneProps {
  onUpload: (
    status: 'accepted' | 'error',
    file: DropZoneFile,
    errorCode?: ErrorCode | string
  ) => void
  onAcceptedFiles?: (files: File[]) => void
  onAllFilesRejected?: () => void
  errorMessage?: string
  maxSize?: number
  loading?: boolean
  multiple?: boolean
  variant?: DropZoneVariant
  className?: string
  disabled?: boolean
  allowCadFiles?: boolean
}

export const DropZone = ({
  onUpload,
  onAcceptedFiles,
  onAllFilesRejected,
  errorMessage,
  maxSize = 60 * 1000000, // 60 mb
  loading,
  multiple = false,
  variant = DropZoneVariant.DOCUMENT,
  className,
  disabled = false,
  allowCadFiles = false,
}: DropZoneProps): JSX.Element => {
  const { translate } = useAppTranslation()

  // Filter out hidden/system files like .DS_Store
  const isHiddenOrSystemFile = (file: File): boolean => {
    // Only hiding .DS_Store (Mac) for now, but can be extended with more patterns if needed
    const hiddenPatterns = [/^\.DS_Store$/i]
    const fileName = file.name
    return hiddenPatterns.some((pattern) => pattern.test(fileName))
  }

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejects: FileRejection[]) => {
      // handle rejections
      const filteredFileRejects =
        variant === DropZoneVariant.FOLDER_AND_DOCUMENTS
          ? fileRejects.filter((_file) => !isHiddenOrSystemFile(_file.file))
          : fileRejects
      filteredFileRejects.forEach((_reject) => {
        const _errorCode =
          _reject.errors.length > 0 ? _reject.errors[0].code : undefined
        onUpload(
          'error',
          {
            base64: '',
            name: _reject.file.name,
            type: _reject.file.type,
            uri: '',
            size: _reject.file.size,
          },
          _errorCode
        )
      })

      // Filter out hidden/system files
      const filteredFiles =
        variant === DropZoneVariant.FOLDER_AND_DOCUMENTS
          ? acceptedFiles.filter((_file) => !isHiddenOrSystemFile(_file))
          : acceptedFiles
      if (filteredFiles.length === 0 && filteredFileRejects.length > 0) {
        onAllFilesRejected?.()
      }

      if (onAcceptedFiles) {
        if (acceptedFiles.length > 0) {
          onAcceptedFiles(acceptedFiles)
        }
        return
      }

      // handle accepted files
      const promises = filteredFiles.map((_file) => toBase64(_file))
      const result = await Promise.allSettled(promises)
      const fulfilledPromises = result.filter(
        (_result) => _result.status === 'fulfilled'
      )

      fulfilledPromises.forEach((_result, index) => {
        const file = filteredFiles[index] as FileWithPath
        const { size } = file
        const type = getFileMimeType(file)
        const uri = URL.createObjectURL(file)
        if (variant === DropZoneVariant.FOLDER_AND_DOCUMENTS) {
          let path = file.path || file.webkitRelativePath || file.name
          // Removing first slash if it exists (can happen when doing drag and drop)
          if (path.startsWith('/')) {
            path = path.slice(1)
          }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onUpload('accepted', { ..._result.value, type, uri, path, size })
        } else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onUpload('accepted', { ..._result.value, type, uri, size })
        }
      })
    },
    [onAcceptedFiles, onAllFilesRejected, onUpload, variant]
  )

  const getAcceptedMimeTypes = (): { [key: string]: string[] } => {
    const imageMimeTypes = {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg'],
      'image/jpg': ['.jpg'],
      'image/webp': ['.webp'],
    }
    const excelMimeTypes = {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
    }
    const documentMimeTypes = {
      ...excelMimeTypes,
      'application/pdf': ['.pdf'],
      'application/rtf': ['.rtf'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        ['.pptx'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.template':
        ['.dotx'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'application/zip': ['.zip'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
      'application/vnd.oasis.opendocument.text': ['.odt'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg'],
      'image/jpg': ['.jpg'],
      'image/webp': ['.webp'],
      'image/svg': ['.svg'],
      'image/tiff': ['.tif', '.tiff'],
      'application/vnd.apple.pages': ['.pages'],
      'application/vnd.apple.keynote': ['.key'],
      'application/vnd.apple.numbers': ['.numbers'],
    }
    const cadMimeTypes: { [key: string]: string[] } = allowCadFiles
      ? { 'image/vnd.dwg': ['.dwg'] }
      : {}
    // TODO : Add mimetypes for document_and_folders variant
    const folderAndDocumentMimeTypes = {
      ...imageMimeTypes,
      ...documentMimeTypes,
      ...cadMimeTypes,
      'text/markdown': ['.md'],
    }
    switch (variant) {
      case DropZoneVariant.MEDIA:
        return imageMimeTypes
      case DropZoneVariant.DOCUMENT:
        return { ...documentMimeTypes, ...cadMimeTypes }
      case DropZoneVariant.EXCEL:
        return excelMimeTypes
      case DropZoneVariant.FOLDER_AND_DOCUMENTS:
        return folderAndDocumentMimeTypes
      default:
        return {}
    }
  }

  const getAcceptedFileFormats = (): string[] => {
    return flatten(Object.values(getAcceptedMimeTypes()))
      .map((_format) => _format.slice(1))
      .sort((a, b) => sortStringsAlphabetically(a, b))
  }

  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      onDrop,
      multiple,
      maxSize,
      accept: getAcceptedMimeTypes(),
      disabled,
    })

  const dragClassNames = useMemo(
    () => `
    ${isDragAccept || isDragReject ? 'bg-gray-100' : ''}
    ${isDragAccept ? 'border-green' : ''}
    ${isDragReject ? 'border-red' : ''}
    `,
    [isDragAccept, isDragReject]
  )

  return (
    <div
      {...getRootProps({ className: 'dropzone' })}
      className={`border-1 relative rounded-md border border-dashed bg-gray-50 p-6 md:p-10
      ${errorMessage ? 'border-red' : 'border-neutral-80'}
      ${disabled ? 'cursor-not-allowed opacity-50' : ''}
      ${dragClassNames}
      ${className ?? ''}`}
    >
      {loading && (
        <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
          <SpinnerWrapper wrapperClassName="items-center" />
        </div>
      )}
      <div
        className={`flex w-full flex-col items-center text-center ${
          loading ? 'opacity-0' : ''
        }`}
      >
        <input
          {...getInputProps()}
          {...(variant === DropZoneVariant.FOLDER_AND_DOCUMENTS && {
            webkitdirectory: 'true',
          })}
        />
        <Icon icon={IconChoices.CLOUD_UPLOAD} size={52} color="#999999" />
        <p style={theme.textVariants.baseBold}>
          {variant === DropZoneVariant.FOLDER_AND_DOCUMENTS
            ? translate('dropZone.dragFolderHere')
            : translate('dropZone.dragFileHere')}{' '}
          <span className="underline hover:cursor-pointer">
            {variant === DropZoneVariant.FOLDER_AND_DOCUMENTS
              ? translate('dropZone.clickToSelectFolder')
              : translate('dropZone.clickToSelect')}
          </span>
        </p>
        <p style={theme.textVariants.caption} className="mt-1 text-gray-500">
          {translate('dropZone.acceptedFormats')}:{' '}
          {getAcceptedFileFormats().join(', ').toUpperCase()}
        </p>
        {variant === DropZoneVariant.FOLDER_AND_DOCUMENTS && (
          <>
            <p
              style={theme.textVariants.caption}
              className="mt-5 text-gray-500"
            >
              {translate('dropZone.folderDescriptionNotePrefix')}
              <span className="font-bold">
                {translate('dropZone.folderDescriptionName')}
              </span>
              {translate('dropZone.folderDescriptionNoteSuffix')}
            </p>
            <p style={theme.textVariants.caption} className=" text-gray-500">
              {translate('dropZone.folderThumbnailNotePrefix')}
              <span className="font-bold">
                {translate('dropZone.folderThumbnailName')}
              </span>
              {translate('dropZone.folderThumbnailNoteSuffix')}
            </p>
          </>
        )}
        {errorMessage ? (
          <p style={theme.textVariants.caption} className="mt-1 text-red">
            {errorMessage}
          </p>
        ) : null}
      </div>
    </div>
  )
}
